'use client';

import { useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@kit/ui/button';
import { Form } from '@kit/ui/form';
import { Trans } from '@kit/ui/trans';
import type { MediaPickerLibraryItem, UploadResult } from '@kit/uploads';

import { useRecordTerminology } from '../../hooks/use-record-terminology';
import { recordTypeOf } from '../../lib/record-terminology';
import {
  UpdateAuditionSchema,
  type UpdateAuditionInput,
} from '../../schemas/audition.schema';
import {
  deleteAuditionAction,
  updateAuditionAction,
} from '../../server/actions/audition.actions';
import type { AuditionWithDates } from '../../types/audition.types';
import { ApplicationSettingsTab } from './application-settings-tab';
import { BasicInfoTab } from './basic-info-tab';
import { CompetitionNetworkingTab } from './competition-networking-tab';
import { ContractTab } from './contract-tab';
import { DatesTab } from './dates-tab';
import { RequirementsTab } from './requirements-tab';
import { getVisibleTabs, type AuditionFormTabDefinition } from './tab-registry';

/**
 * Format error for display in toast
 */
function formatError(error: string | Record<string, unknown>): string {
  if (typeof error === 'string') {
    return error;
  }
  // Handle Zod flattened errors
  if (error.formErrors && Array.isArray(error.formErrors)) {
    return (error.formErrors as string[]).join(', ') || 'Validation error';
  }
  return 'An error occurred';
}

interface AuditionFormProps {
  audition: AuditionWithDates;
  accountSlug: string;
  /**
   * The organization's media library (photos + video) + org id, for the
   * competition cover MediaPicker. Server-resolved and threaded down to the
   * Basic-info section. `null` for non-competitions or accounts with no org.
   */
  orgMedia?: { organizationId: string; items: MediaPickerLibraryItem[] } | null;
  /**
   * Cover upload action, injected from the app layer (the org-media upload
   * adapter lives in `apps/web` and cannot be imported by this package). Wraps
   * `orgMediaUploadAdapter` with the org id + slot. `undefined` disables upload.
   */
  coverUploadAction?: (formData: FormData) => Promise<UploadResult>;
  /**
   * Whether the current user is a super-admin. Gates the editorial `featured`
   * toggle in the settings tab (server-side write is enforced separately in
   * `updateAuditionAction`). Defaults to `false` — non-super-admins never see
   * the toggle.
   */
  isSuperAdmin?: boolean;
}

export function AuditionForm({
  audition,
  accountSlug,
  orgMedia,
  coverUploadAction,
  isSuperAdmin = false,
}: AuditionFormProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const recordType = recordTypeOf(audition);
  const { t: term } = useRecordTerminology(recordType);
  const visibleTabs = getVisibleTabs(recordType);

  // Active section is URL-driven (`?section=`) so the persistent SettingsSidebar
  // (rendered by RecordSettingsShell, outside this form) and the form agree, and
  // the section survives navigation from a sibling config route. Falls back to
  // the first visible section for a missing/invalid param.
  const searchParams = useSearchParams();
  const requestedSection = searchParams.get('section');
  const activeTab: AuditionFormTabDefinition['id'] =
    visibleTabs.find((tab) => tab.id === requestedSection)?.id ??
    visibleTabs[0]?.id ??
    'basic';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isCompetition = recordType === 'competition';

  const branding = (audition.competition_branding ?? null) as {
    cover_media_id?: string;
    accent_color?: string;
    website_url?: string;
    livestream_embed_url?: string;
    support_button_label?: string;
    support_button_destination?: string;
  } | null;

  // Competition fields are seeded into the form ONLY for competitions. For a
  // plain audition the competition tabs never render, so leaving these
  // `undefined` keeps `form.handleSubmit` from submitting empty branding /
  // networking values that the update mapper would then write to the row
  // (the mapper guards every competition field with `!== undefined`). This
  // preserves the pre-merge behaviour where auditions never carried these.
  const competitionDefaults: Partial<UpdateAuditionInput> = isCompetition
    ? {
        autoAcceptRegistrations: audition.auto_accept_registrations ?? false,
        hideApplyButton: audition.hide_apply_button ?? false,
        hideIndividualParticipants:
          audition.hide_individual_participants ?? false,
        hideOrganizationParticipants:
          audition.hide_organization_participants ?? false,
        participantsVisibleToPartners:
          audition.participants_visible_to_partners ?? false,
        competitionBranding: {
          cover_media_id: branding?.cover_media_id ?? '',
          accent_color: branding?.accent_color ?? '',
          website_url: branding?.website_url ?? '',
          livestream_embed_url: branding?.livestream_embed_url ?? '',
          support_button_label: branding?.support_button_label ?? '',
          support_button_destination:
            branding?.support_button_destination ?? '',
        },
        isNetworkingEvent: audition.is_networking_event ?? false,
        hasFinalShow: audition.has_final_show ?? false,
        // BAL-408: defaults true (tab shown) when the column is null/unset.
        showPartnerMeetings: audition.show_partner_meetings ?? true,
        networkingAllowOrgSelection:
          audition.networking_allow_org_selection ?? false,
        networkingAllowIndividualSelection:
          audition.networking_allow_individual_selection ?? false,
        networkingOrgSelectionStart: audition.networking_org_selection_start
          ? new Date(audition.networking_org_selection_start)
          : null,
        networkingOrgSelectionEnd: audition.networking_org_selection_end
          ? new Date(audition.networking_org_selection_end)
          : null,
        networkingIndividualSelectionStart:
          audition.networking_individual_selection_start
            ? new Date(audition.networking_individual_selection_start)
            : null,
        networkingIndividualSelectionEnd:
          audition.networking_individual_selection_end
            ? new Date(audition.networking_individual_selection_end)
            : null,
        networkingShowMatchesToOrgs:
          audition.networking_show_matches_to_orgs ?? false,
        networkingShowMatchesToIndividuals:
          audition.networking_show_matches_to_individuals ?? false,
      }
    : {};

  const form = useForm<UpdateAuditionInput>({
    resolver: zodResolver(UpdateAuditionSchema),
    defaultValues: {
      title: audition.title || '',
      description: audition.description || '',
      auditionType: audition.audition_type as 'public' | 'private' | undefined,
      externalLink: audition.external_link || '',
      requirementGenders: audition.requirement_genders || [],
      requirementAgeMin: audition.requirement_age_min || undefined,
      requirementAgeMax: audition.requirement_age_max || undefined,
      requirementHeightMaleMin:
        audition.requirement_height_male_min || undefined,
      requirementHeightMaleMax:
        audition.requirement_height_male_max || undefined,
      requirementHeightFemaleMin:
        audition.requirement_height_female_min || undefined,
      requirementHeightFemaleMax:
        audition.requirement_height_female_max || undefined,
      requirementOther: audition.requirement_other || '',
      requirementDancePositions: audition.requirement_dance_positions || [],
      contractLength: audition.contract_length as
        | 'life'
        | 'seasonal'
        | 'shortTerm'
        | undefined,
      contractStartDate: audition.contract_start_date
        ? new Date(audition.contract_start_date)
        : undefined,
      contractEndDate: audition.contract_end_date
        ? new Date(audition.contract_end_date)
        : undefined,
      openingDate: audition.opening_date
        ? new Date(audition.opening_date)
        : undefined,
      closingDate: audition.closing_date
        ? new Date(audition.closing_date)
        : undefined,
      autoClose: audition.auto_close ?? true,
      applyOnApp: audition.apply_on_app ?? false,
      byInvitationOnly: audition.by_invitation_only ?? false,
      // Entrants may edit after acceptance unless the organizer locks it.
      // Applies to all auditions, so it lives in the base defaults (not the
      // competition-only block). Defaults true to match the DB column.
      allowAcceptedEdits: audition.allow_accepted_edits ?? true,
      // Editorial pin-to-top flag. Seeded so a super-admin save round-trips the
      // current value; the toggle only renders (and only persists) for
      // super-admins (server-gated in updateAuditionAction).
      featured: audition.featured ?? false,
      accessCode: audition.access_code ?? '',
      videoPreSelection: audition.video_pre_selection ?? false,
      applicationFeeAmount: audition.application_fee_amount
        ? Number(audition.application_fee_amount)
        : undefined,
      applicationFeeCurrency: audition.application_fee_currency || 'USD',
      additionalInformations: audition.additional_informations || '',

      ...competitionDefaults,
    },
  });

  const onSubmit = async (data: UpdateAuditionInput) => {
    setIsSubmitting(true);
    try {
      const result = await updateAuditionAction(audition.id, data);
      if (result.success) {
        toast.success(term('saved'));
        router.refresh();
      } else {
        toast.error(formatError(result.error));
      }
    } catch {
      toast.error(t('auditions:formTabs.common.unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(term('deleteConfirm'))) {
      return;
    }

    setIsDeleting(true);
    try {
      const result = await deleteAuditionAction(audition.id);
      if (result.success) {
        toast.success(term('deleted'));
        router.push(`/home/${accountSlug}/auditions`);
      } else {
        toast.error(formatError(result.error));
      }
    } catch {
      toast.error(t('auditions:formTabs.common.unexpectedError'));
    } finally {
      setIsDeleting(false);
    }
  };

  const renderTabContent = (id: AuditionFormTabDefinition['id']) => {
    switch (id) {
      case 'basic':
        return (
          <BasicInfoTab
            form={form}
            audition={audition}
            orgMedia={orgMedia}
            coverUploadAction={coverUploadAction}
          />
        );
      case 'requirements':
        return <RequirementsTab form={form} />;
      case 'dates':
        return <DatesTab audition={audition} />;
      case 'contract':
        return <ContractTab form={form} />;
      case 'applications':
        return (
          <ApplicationSettingsTab
            form={form}
            audition={audition}
            isSuperAdmin={isSuperAdmin}
          />
        );
      case 'competitionNetworking':
        return <CompetitionNetworkingTab form={form} />;
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {/* The Settings sidebar lives in RecordSettingsShell (outside this
            form) and drives the active section via `?section=`; this form just
            renders the active section's content. (BAL-386) */}
        {renderTabContent(activeTab)}

        {/* Primary actions — single-purpose footer, right-aligned. Publish
            now lives on the record Overview (one decisive CTA per viewport);
            this footer owns Save only. */}
        <div className="flex items-center justify-end gap-2 border-t pt-6">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Trans i18nKey="auditions:editor.save" />
          </Button>
        </div>

        {/* Danger Zone — visually + structurally separated from save.
            Mirrors the personal-account settings danger zone pattern so
            destructive actions never share a row with everyday saves. */}
        <div className="border-border/60 mt-12 border-t pt-8">
          <div className="flex flex-col gap-y-1">
            <span className="text-sm font-medium">
              <Trans i18nKey="auditions:editor.dangerZone.title" />
            </span>
            <p className="text-muted-foreground text-sm">
              <Trans i18nKey="auditions:editor.dangerZone.body" />
            </p>
          </div>
          <div className="mt-4">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {term('delete')}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
